import { Component, inject, signal, computed } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CookieConsentService, ConsentPreferences } from '../../core/cookie-consent/cookie-consent.service';

@Component({
  selector: 'app-cookie-consent-banner',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (consent.showBanner()) {
    <div class="cookie-banner" role="dialog" aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-desc">
      <div class="cookie-banner-inner">
        <div class="cookie-banner-content">
          <h2 id="cookie-banner-title" class="cookie-banner-title">We use cookies</h2>
          <p id="cookie-banner-desc" class="cookie-banner-desc">
            We use cookies and similar technologies to keep you signed in, secure the site, and remember your preferences.
            See our <a routerLink="/legal/cookies">Cookie Policy</a> and <a routerLink="/legal/privacy">Privacy Policy</a> for details.
          </p>
        </div>
        <div class="cookie-banner-actions">
          <button type="button" class="btn btn-essential" (click)="consent.acceptEssential()">
            Essential only
          </button>
          <button type="button" class="btn btn-all" (click)="consent.acceptAll()">
            Accept all
          </button>
          <button type="button" class="btn btn-customise" (click)="openCustomise()">
            Customise
          </button>
          <a routerLink="/legal/cookies" class="btn btn-link">More info</a>
        </div>
      </div>
    </div>
    }

    @if (consent.customiseOpen()) {
      <div class="customise-overlay" (click)="closeCustomise()" role="presentation">
        <div class="customise-modal" (click)="$event.stopPropagation()" role="dialog" aria-labelledby="customise-title">
          <h2 id="customise-title" class="customise-title">Cookie preferences</h2>
          <p class="customise-desc">Choose which cookies you allow. Essential cookies are required for the site to work.</p>

          <div class="cookie-rows">
            <div class="cookie-row locked">
              <div class="cookie-info">
                <span class="cookie-name">Essential</span>
                <span class="cookie-desc">Required for authentication, security, and core functionality.</span>
              </div>
              <div class="cookie-toggle locked" aria-label="Essential cookies (always on)">
                <span class="toggle-checked">Always on</span>
              </div>
            </div>
            <div class="cookie-row">
              <div class="cookie-info">
                <span class="cookie-name">Functional</span>
                <span class="cookie-desc">Theme, sidebar state, and preferences. Improves your experience.</span>
              </div>
              <label class="cookie-toggle">
                <input type="checkbox" [checked]="editable().functional" (change)="toggle('functional', $event)">
                <span class="toggle-checkbox"></span>
              </label>
            </div>
            <div class="cookie-row">
              <div class="cookie-info">
                <span class="cookie-name">Analytics</span>
                <span class="cookie-desc">Helps us understand how the site is used. Not currently used.</span>
              </div>
              <label class="cookie-toggle">
                <input type="checkbox" [checked]="editable().analytics" (change)="toggle('analytics', $event)">
                <span class="toggle-checkbox"></span>
              </label>
            </div>
            <div class="cookie-row">
              <div class="cookie-info">
                <span class="cookie-name">Marketing</span>
                <span class="cookie-desc">Used for advertising. Not currently used.</span>
              </div>
              <label class="cookie-toggle">
                <input type="checkbox" [checked]="editable().marketing" (change)="toggle('marketing', $event)">
                <span class="toggle-checkbox"></span>
              </label>
            </div>
          </div>

          <div class="customise-actions">
            <button type="button" class="btn btn-essential" (click)="closeCustomise()">Cancel</button>
            <button type="button" class="btn btn-all" (click)="saveCustomise()">Save preferences</button>
          </div>
        </div>
      </div>
    }
  `,
  styles: `
    .cookie-banner {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      /* Design-system z-index scale (src/sass/_tokens.scss): base 0 < sticky
       * 100 < drawer 200 < overlay 1000 < modal 1100 < toast 1200. This used
       * to be a hard-coded 9998/9999 that sat ABOVE every modal in the app,
       * including the public share page's "Approve payment request" modal —
       * with the banner still visible, its full-page overlay intercepted
       * every click meant for the modal underneath (unclickable-overlay
       * bug). The banner is a persistent, low-urgency prompt; it must never
       * out-rank a transient blocking dialog, so it sits just above
       * sticky/drawer chrome but below the overlay/modal/toast layers.
       */
      z-index: var(--rp-z-drawer, 200);
      padding: 16px 20px max(16px, env(safe-area-inset-bottom));
      background: var(--bg-elevated, #fff);
      border-top: 1px solid var(--border-soft, #e2e8f0);
      box-shadow: 0 -4px 24px rgba(15, 23, 42, 0.08);
    }
    /* Compact footprint on small screens so the fixed banner crowds less of
     * whatever page content sits behind it (e.g. the last card on a public
     * approval page). */
    @media (max-width: 480px) {
      .cookie-banner {
        padding: 12px 14px max(12px, env(safe-area-inset-bottom));
      }
      .cookie-banner-title { font-size: 14px; }
      .cookie-banner-desc { font-size: 12px; }
      .cookie-banner-actions .btn { padding: 8px 14px; font-size: 13px; min-block-size: var(--rp-tap-min); }
    }
    .cookie-banner-inner {
      max-width: 960px;
      margin: 0 auto;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }
    .cookie-banner-content { flex: 1; min-width: 240px; }
    .cookie-banner-title { margin: 0 0 4px 0; font-size: 16px; font-weight: 600; color: var(--text-primary, #0f172a); }
    .cookie-banner-desc { margin: 0; font-size: 14px; line-height: 1.5; color: var(--text-secondary, #475569); }
    .cookie-banner-desc a { color: #2563eb; text-decoration: none; font-weight: 500; }
    .cookie-banner-desc a:hover { text-decoration: underline; }
    .cookie-banner-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
    .btn {
      padding: 10px 18px;
      min-block-size: var(--rp-tap-min);
      display: inline-flex;
      align-items: center;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      border: none;
      transition: background 0.15s, color 0.15s;
    }
    .btn-essential {
      background: #f1f5f9;
      color: #334155;
    }
    .btn-essential:hover { background: #e2e8f0; }
    .btn-all {
      background: #1e40af;
      color: white;
    }
    .btn-all:hover { background: #1d4ed8; }
    .btn-customise {
      background: #f1f5f9;
      color: #334155;
      border: 1px solid #e2e8f0;
    }
    .btn-customise:hover { background: #e2e8f0; }
    .btn-link {
      background: transparent;
      color: #64748b;
      text-decoration: none;
      font-weight: 500;
    }
    .btn-link:hover { color: #334155; }

    .customise-overlay {
      position: fixed;
      inset: 0;
      /* Same rationale as .cookie-banner above — must stay below the app's
       * modal layer (--rp-z-modal, 1100) so a real dialog opened on top of
       * it (or after it) is always clickable. */
      z-index: var(--rp-z-overlay, 1000);
      background: rgba(15, 23, 42, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }
    .customise-modal {
      background: #fff;
      border-radius: 12px;
      padding: 24px;
      max-width: 480px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
    }
    .customise-title { margin: 0 0 8px 0; font-size: 1.25rem; font-weight: 700; color: #0f172a; }
    .customise-desc { margin: 0 0 20px 0; font-size: 14px; color: #64748b; line-height: 1.5; }
    .cookie-rows { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
    .cookie-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding: 12px;
      background: #f8fafc;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
    }
    .cookie-row.locked { background: #f1f5f9; }
    .cookie-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
    .cookie-name { font-weight: 600; font-size: 14px; color: #1e293b; }
    .cookie-desc { font-size: 13px; color: #64748b; line-height: 1.4; }
    .cookie-toggle {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
    }
    .cookie-toggle.locked { cursor: default; }
    .cookie-toggle input { position: absolute; opacity: 0; width: 0; height: 0; }
    .toggle-checkbox {
      width: 44px;
      height: 24px;
      border-radius: 12px;
      background: #cbd5e1;
      position: relative;
      transition: background 0.2s;
    }
    .toggle-checkbox::after {
      content: '';
      position: absolute;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #fff;
      top: 2px;
      left: 2px;
      box-shadow: 0 1px 3px rgba(0,0,0,.2);
      transition: transform 0.2s;
    }
    .cookie-toggle input:checked + .toggle-checkbox::after { transform: translateX(20px); }
    .cookie-toggle input:checked + .toggle-checkbox { background: #2563eb; }
    .cookie-toggle input:focus-visible + .toggle-checkbox { outline: 2px solid #2563eb; outline-offset: 2px; }
    .toggle-checked { font-size: 12px; font-weight: 600; color: #64748b; }
    .customise-actions { display: flex; justify-content: flex-end; gap: 10px; }
  `
})
export class CookieConsentBanner {
  readonly consent = inject(CookieConsentService);
  protected editable = signal<ConsentPreferences>(this.consent.getEditablePreferences());

  openCustomise(): void {
    this.editable.set(this.consent.getEditablePreferences());
    this.consent.openCustomise();
  }

  closeCustomise(): void {
    this.consent.closeCustomise();
  }

  toggle(cat: 'functional' | 'analytics' | 'marketing', ev: Event): void {
    const checked = (ev.target as HTMLInputElement).checked;
    this.editable.update(p => ({ ...p, [cat]: checked }));
  }

  saveCustomise(): void {
    this.consent.setPreferences(this.editable());
  }
}
